import { useEffect, useRef, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { adminApi } from "../api";

/**
 * Universal entity picker (cliente|produto|pack|curso) powered by
 * /api/admin/ops/pick/{entity}. Stores numeric id in `value`, resolves a label
 * by querying the first match for that id when value changes externally.
 */
export default function EntityPicker({ entity, value, onChange, onPick, placeholder = "Pesquisar...", testid }) {
  const [label, setLabel] = useState("");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  // Resolve label when id changes externally
  useEffect(() => {
    let cancelled = false;
    if (!value) { setLabel(""); return; }
    adminApi.pick(entity, String(value), 1)
      .then((r) => {
        if (cancelled) return;
        const hit = (r.options || []).find((o) => String(o.id) === String(value));
        setLabel(hit ? hit.label : "");
      })
      .catch(() => { if (!cancelled) setLabel(""); });
    return () => { cancelled = true; };
  }, [entity, value]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await adminApi.pick(entity, q, 25);
        setOpts(r.options || []);
      } catch (e) { setOpts([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [open, q, entity]);

  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const pick = (o) => {
    onChange(o.id);
    if (onPick) onPick(o);
    setLabel(o.label || "");
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="flex items-stretch gap-2">
        <input
          type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          placeholder="ID" data-testid={`${testid}-id`}
          className="w-28 px-3 py-2 text-sm border border-[#E6E1D6] rounded font-mono outline-none focus:border-[#0F1829]"
        />
        {label && (
          <span className="flex-1 px-3 py-2 text-sm border border-dashed border-[#E6E1D6] rounded bg-[#FAF8F2] text-[#374151] truncate"
                title={label} data-testid={`${testid}-label`}>
            {label}
          </span>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)}
          data-testid={`${testid}-search-btn`}
          className="px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#F5F2EB] flex items-center gap-1 whitespace-nowrap">
          <Search size={13} /> Pesquisar
        </button>
      </div>

      {open && (
        <div data-testid={`${testid}-popover`}
          className="absolute z-50 mt-1 left-0 w-[28rem] max-w-[calc(100vw-2rem)] bg-white border border-[#E6E1D6] rounded-md shadow-xl overflow-hidden">
          <div className="p-2 border-b border-[#E6E1D6] relative">
            <Search size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#978D78] pointer-events-none" />
            <input
              ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder} data-testid={`${testid}-search-input`}
              className="w-full pl-8 pr-8 py-1.5 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829]"
            />
            {q && (
              <button type="button" onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#978D78] hover:text-[#0F1829]">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading && (
              <div className="px-3 py-4 text-center text-xs text-[#6B7280]">
                <Loader2 size={14} className="inline animate-spin mr-1" /> A pesquisar...
              </div>
            )}
            {!loading && opts.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-[#6B7280]">Sem resultados.</div>
            )}
            {!loading && opts.map((o) => (
              <button key={`pick-${o.id}`} type="button" onClick={() => pick(o)}
                data-testid={`${testid}-opt-${o.id}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#F5F2EB] border-b border-[#F3F0E7] last:border-0 flex items-baseline gap-2">
                <span className="font-mono text-[11px] text-[#978D78] w-14 shrink-0">#{o.id}</span>
                <span className="flex-1 truncate">{o.label || <span className="text-[#9CA3AF]">—</span>}</span>
                {o.extra && <span className="text-[10px] font-mono text-[#978D78]">{o.extra}</span>}
              </button>
            ))}
          </div>
          <div className="px-3 py-1.5 bg-[#FAF8F2] border-t border-[#E6E1D6] text-[10px] text-[#978D78] uppercase tracking-[0.15em]">
            Até 25 resultados
          </div>
        </div>
      )}
    </div>
  );
}
